import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import StudioPageShell from '../../components/studio/StudioPageShell';
import StudioV2StarterGallery from '../../components/studio/StudioV2StarterGallery';
import {
  createV2Flow, deleteV2Flow, getV2Flow, getV2FlowDiagnosticsCounts, listV2Apps, listV2Flows, studioV2NewFlowDraft,
} from '../../api/studioV2.api';
import { legacyFlowToV2Client } from '../../utils/legacyFlowToV2Client';
import { normalizeV2FlowEdge } from '../../components/studio/studioV2FlowModel';
import FlowMiniDiagram from '../../components/studio/flowBuilder/FlowMiniDiagram';
import NewFlowWizard from '../../components/studio/flowBuilder/NewFlowWizard';
import { flowUsage, stepCount } from '../../components/studio/flowBuilder/flowBuilderModel';

const STATUS_CHIP = {
  published: { label: 'Ready', color: 'success' },
  draft: { label: 'Draft', color: 'warning' },
  archived: { label: 'Archived', color: 'default' },
};

function relativeTime(date) {
  if (!date) return '';
  const diff = Date.now() - new Date(date).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(date).toLocaleDateString();
}

const statusOf = (row) => row.definition?.status || row.status || 'draft';

function FlowCard({
  row, usedBy, busy, onDuplicate, onDelete,
}) {
  const def = row.definition || {};
  const name = row.name || def.name || 'Untitled';
  const status = STATUS_CHIP[statusOf(row)] || STATUS_CHIP.draft;
  const steps = stepCount(def);
  const [appsAnchor, setAppsAnchor] = useState(null);

  return (
    <Paper
      elevation={0}
      data-testid={`flow-card-${row._id}`}
      sx={{
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow .15s, transform .15s',
        '&:hover': { boxShadow: '0 10px 28px rgba(15,23,42,.10)', transform: 'translateY(-2px)' },
      }}
    >
      <Box
        component={RouterLink}
        to={`/studio/flows/${row._id}`}
        aria-label={`Edit ${name}`}
        sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <Box sx={{ p: 1.5, pb: 0 }}>
          <FlowMiniDiagram definition={def} height={112} />
        </Box>
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{name}</Typography>
            <Chip size="small" label={status.label} color={status.color} variant="outlined" sx={{ height: 22, fontWeight: 600 }} />
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              minHeight: 40, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}
          >
            {def.description || 'No description yet.'}
          </Typography>
          <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.25 }}>
            <Chip size="small" icon={<AccountTreeIcon />} label={`${steps} ${steps === 1 ? 'step' : 'steps'}`} sx={{ height: 24 }} />
          </Stack>
        </Box>
      </Box>
      <Box sx={{ px: 2, pb: 0.5, minHeight: 28 }}>
        {usedBy.length > 0 ? (
          <>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              icon={<ViewModuleOutlinedIcon />}
              label={`Used in ${usedBy.length} ${usedBy.length === 1 ? 'app' : 'apps'}`}
              onClick={(e) => setAppsAnchor(e.currentTarget)}
              aria-haspopup="menu"
              sx={{ height: 24 }}
              data-testid="flow-card-used-by"
            />
            <Menu anchorEl={appsAnchor} open={Boolean(appsAnchor)} onClose={() => setAppsAnchor(null)}>
              {usedBy.map((a) => (
                <MenuItem key={a.id} component={RouterLink} to={`/studio/apps/${a.id}`} data-testid="flow-card-app-link">
                  <ListItemText primary={a.name} secondary={a.roles.join(', ')} />
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Typography variant="caption" color="text.disabled">Not used in any app yet</Typography>
        )}
      </Box>
      <Stack direction="row" alignItems="center" sx={{ px: 2, pb: 1.25, mt: 'auto' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {row.updatedAt ? `Edited ${relativeTime(row.updatedAt)}` : ''}
          {row.accessRole && row.accessRole !== 'owner' ? ' · Shared with you' : ''}
        </Typography>
        <Tooltip title="Duplicate">
          <span>
            <IconButton size="small" aria-label="Duplicate flow" disabled={busy} onClick={() => onDuplicate(row._id)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {(!row.accessRole || row.accessRole === 'owner') && (
          <Tooltip title="Delete">
            <span>
              <IconButton size="small" aria-label="Delete flow" disabled={busy} onClick={() => onDelete(row, usedBy)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {busy && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </Stack>
    </Paper>
  );
}

function StudioFlowsListPage() {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [diagCounts, setDiagCounts] = useState({ ownedCount: 0, tenantCount: 0 });
  const [starterError, setStarterError] = useState(null);
  const importRef = useRef(null);

  const usage = useMemo(() => flowUsage(apps), [apps]);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [{ flows: rows, error: err }, { apps: appRows }] = await Promise.all([listV2Flows(), listV2Apps()]);
    if (err) setError(err);
    setFlows(rows || []);
    setApps(appRows || []);
    if (!err && (!rows || rows.length === 0)) {
      setDiagCounts(await getV2FlowDiagnosticsCounts());
    } else {
      setDiagCounts({ ownedCount: 0, tenantCount: 0 });
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return flows.filter((row) => {
      if (statusFilter !== 'all' && statusOf(row) !== statusFilter) return false;
      if (!q) return true;
      const hay = `${row.name || ''} ${row.definition?.name || ''} ${row.definition?.description || ''} ${row._id} ${row.definition?.id || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [flows, query, statusFilter]);

  const onDuplicate = async (mongoId) => {
    setBusyId(mongoId);
    setError(null);
    const { flow, error: gerr } = await getV2Flow(mongoId);
    if (gerr || !flow?.definition) {
      setError(gerr || 'Could not load flow');
      setBusyId(null);
      return;
    }
    const def = flow.definition;
    const baseName = def.name != null && String(def.name).trim() ? String(def.name).trim() : 'Untitled flow';
    const { flow: created, error: cerr } = await createV2Flow({
      ...def,
      id: studioV2NewFlowDraft('x').id,
      name: `Copy of ${baseName}`,
      schemaVersion: 'flow/v2',
    });
    setBusyId(null);
    if (cerr) {
      setError(cerr);
      return;
    }
    if (created?._id) navigate(`/studio/flows/${created._id}`);
  };

  const onDelete = async (row, usedBy) => {
    const name = row.name || row.definition?.name || 'this flow';
    const warning = usedBy.length
      ? `\n\n${usedBy.length === 1 ? 'An app uses' : `${usedBy.length} apps use`} it (${usedBy.map((a) => a.name).join(', ')}). Those panels will stop working.`
      : '';
    if (!window.confirm(`Delete "${name}"? This cannot be undone.${warning}`)) return;
    setBusyId(row._id);
    const { error: derr } = await deleteV2Flow(row._id);
    setBusyId(null);
    if (derr) {
      setError(derr);
      return;
    }
    await load();
  };

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setError(null);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const raw = JSON.parse(String(reader.result || '{}'));
        const def = raw.schemaVersion === 'flow/v2' ? raw : legacyFlowToV2Client(raw);
        const draft = studioV2NewFlowDraft(def.name || 'Imported flow');
        let merged = { ...def, id: draft.id, schemaVersion: 'flow/v2' };
        if (Array.isArray(merged.edges)) {
          merged = { ...merged, edges: merged.edges.map((edge) => normalizeV2FlowEdge(edge)) };
        }
        const { flow, error: err } = await createV2Flow(merged);
        if (err) setError(err);
        else if (flow?._id) navigate(`/studio/flows/${flow._id}`);
        else await load();
      } catch (err) {
        setError(err?.message || 'That file could not be read.');
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  return (
    <StudioPageShell
      title="Flows"
      subtitle="The step-by-step logic behind your apps: what the AI reads, decides and returns."
      backTo="/studio"
      backLabel="Studio"
      maxWidth={1280}
      actions={(
        <>
          <input ref={importRef} type="file" accept="application/json,.json" hidden onChange={onImportFile} />
          <Button
            variant="outlined"
            startIcon={importing ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => importRef.current?.click()}
            disabled={importing}
            sx={{ textTransform: 'none' }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setWizardOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            data-testid="new-flow"
          >
            New flow
          </Button>
        </>
      )}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={36} />
        </Box>
      ) : flows.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 }, borderRadius: 3, border: 1, borderColor: 'divider',
          }}
        >
          <Stack alignItems="center" spacing={1} sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: 'text.secondary', mb: 1,
            }}
            >
              <AccountTreeIcon fontSize="large" />
            </Box>
            <Typography variant="h6" fontWeight={700}>No flows yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mb: 1.5 }}>
              A flow is a series of steps, like “read the answer, score it with AI, return a result”. Start blank, from a template, or describe it to the AI.
            </Typography>
            {diagCounts.tenantCount > 0 && diagCounts.ownedCount === 0 && (
              <Alert severity="warning" sx={{ textAlign: 'left', maxWidth: 560 }}>
                Your workspace has
                {' '}
                <strong>{diagCounts.tenantCount}</strong>
                {' '}
                flows, but none belong to your account. This usually means they were migrated under another user. An admin can run
                {' '}
                <code>npm run repair:v2-flow-owners -- --rehome-all-to-user &lt;your-user-id-or-email&gt;</code>
              </Alert>
            )}
            <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Create your first flow
            </Button>
          </Stack>
          {starterError && <Alert severity="error" onClose={() => setStarterError(null)} sx={{ mb: 2 }}>{starterError}</Alert>}
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Or start from a ready-made example</Typography>
          <StudioV2StarterGallery mode="flows" variant="compact" onError={setStarterError} />
        </Paper>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              placeholder="Search flows"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ width: { sm: 320 }, bgcolor: 'background.paper' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, v) => v && setStatusFilter(v)}>
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.75 }}>All</ToggleButton>
              <ToggleButton value="published" sx={{ textTransform: 'none', px: 1.75 }}>Ready</ToggleButton>
              <ToggleButton value="draft" sx={{ textTransform: 'none', px: 1.75 }}>Drafts</ToggleButton>
              <ToggleButton value="archived" sx={{ textTransform: 'none', px: 1.75 }}>Archived</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2.5 }}>
            {filtered.map((row) => (
              <FlowCard
                key={row._id}
                row={row}
                usedBy={usage.get(String(row._id)) || []}
                busy={busyId === row._id}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
              />
            ))}
          </Box>
          {!filtered.length && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
              No flows match.
            </Typography>
          )}
        </>
      )}
      <NewFlowWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(flow, { assistantPrompt }) => navigate(`/studio/flows/${flow._id}`, { state: assistantPrompt ? { assistantPrompt } : undefined })}
      />
    </StudioPageShell>
  );
}

export default StudioFlowsListPage;
